// src/components/Contact/Contact.jsx
import React, { useRef, useState } from "react";
import { FaEnvelope, FaPhoneAlt, FaPaperPlane, FaHome } from "react-icons/fa";
import { motion } from "framer-motion";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router-dom";
import "./Contact.css";

const FORM_TOKEN_KEY = "gw_form_ok_until";

const Contact = () => {
  const backgroundUrl = `${process.env.PUBLIC_URL}/header-image.jpg`;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [error, setError] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const formRef = useRef();
  const navigate = useNavigate();

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    setError(null);
  };

  const handleConsentChange = (e) => {
    setConsentChecked(e.target.checked);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!consentChecked) {
      setError("Please provide consent to be contacted.");
      setIsLoading(false);
      return;
    }

    if (!captchaToken) {
      setError("Please verify you're not a robot.");
      setIsLoading(false);
      return;
    }

    const formData = new FormData(formRef.current);
    const data = Object.fromEntries(formData.entries());
    data["g-recaptcha-response"] = captchaToken;

    try {
      const response = await fetch("https://formspree.io/f/xpwlpddr", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setIsSubmitted(true);
        formRef.current.reset();
        setCaptchaToken(null);
        setConsentChecked(false);

        const expireAt = Date.now() + 2 * 60 * 1000;
        sessionStorage.setItem(FORM_TOKEN_KEY, String(expireAt));

        setTimeout(
          () =>
            navigate("/confirmation", {
              replace: true,
              state: { from: "form" },
            }),
          800
        );
      } else {
        throw new Error("Formspree submission failed.");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="contactpage-section"
      style={{
        backgroundImage: `linear-gradient(135deg, rgba(9, 41, 72, 0.9) 0%, rgba(2, 174, 239, 0.8) 100%), url(${backgroundUrl})`,
      }}
    >
      <div className="contactpage-container">
        {/* LEFT SIDE */}
        <motion.div
          className="contactpage-info"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="contactpage-content">
            <h2 className="contactpage-title">
              <span className="contactpage-title-gradient">Get In Touch</span>
              <span className="contactpage-title-underline" />
            </h2>
            <p className="contactpage-subtitle">
              Our team is ready to help with questions about pricing,
              scheduling, or which cleaning service is right for your home.
              Tell us a little about your carpets, upholstery, tile, grout, or
              pet odor concerns and we'll point you to the best option for a
              healthier, fresher space.
            </p>

            <div className="contactpage-details-list">
              <motion.div
                className="contactpage-detail"
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="contactpage-icon-wrapper">
                  <FaEnvelope className="contactpage-icon" />
                </div>
                <div className="contactpage-text">
                  <div className="contactpage-label">Email Us</div>
                  <div className="contactpage-value email-value">
                    info@greenworkscarpetcleaning.com
                  </div>
                  <a
                    href="mailto:info@greenworkscarpetcleaning.com"
                    className="contactpage-link"
                  >
                    <span>Send a message</span>
                    <span className="contactpage-link-arrow">→</span>
                  </a>
                </div>
              </motion.div>

              <motion.div
                className="contactpage-detail"
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="contactpage-icon-wrapper">
                  <FaPhoneAlt className="contactpage-icon" />
                </div>
                <div className="contactpage-text">
                  <div className="contactpage-label">Call Us</div>
                  <div className="contactpage-value">+1 (206) 899-6862</div>
                  <a
                    href="tel:+1 (206) 899-6862"
                    className="contactpage-link"
                  >
                    <span>Call now</span>
                    <span className="contactpage-link-arrow">→</span>
                  </a>
                </div>
              </motion.div>
            </div>

            <div className="contactpage-extra">
              <h3 className="contactpage-extra-heading">
                How Greenworks Can Help
              </h3>
              <p className="contactpage-extra-text">
                Whether you need a one-time deep clean before guests arrive, a
                move-out cleaning, or ongoing maintenance for busy family
                spaces, we're here to make the process simple. Share a few
                details about your rooms, traffic levels, and problem areas so
                we can prepare an accurate quote.
              </p>
              <p className="contactpage-extra-text">
                We regularly serve homeowners and renters throughout Lynnwood,
                Everett, Bothell, Mill Creek, Shoreline, and nearby communities.
                Our technicians use eco-friendly products that are safe for
                children and pets while still delivering powerful results on
                stains, odors, and built-up soil.
              </p>
              <ul className="contactpage-extra-list">
                <li>
                  Ask about carpet, rug, upholstery, tile and grout, or pet
                  odor treatments in the same visit.
                </li>
                <li>
                  Request same-day or next-day appointments when your schedule
                  allows.
                </li>
                <li>
                  Learn what to expect before, during, and after your cleaning
                  so you can plan around drying times.
                </li>
              </ul>
              <p className="contactpage-extra-text">
                Use the form to tell us what areas you'd like cleaned and the
                best time to reach you. A member of our team will review your
                information and follow up with clear next steps, pricing, and
                available time slots.
              </p>
            </div>
          </div>
        </motion.div>

        {/* RIGHT FORM */}
        <motion.div
          className="contactpage-form-wrapper"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <form
            className="contactpage-form"
            onSubmit={handleSubmit}
            ref={formRef}
          >
            <div className="contactpage-form-decoration contactpage-form-decoration-1" />
            <div className="contactpage-form-decoration contactpage-form-decoration-2" />

            <div className="contactpage-form-header">
              <h3>Send Us a Message</h3>
              <p className="contactpage-form-caption">
                Fill out the form with a brief description of your home and the
                areas you'd like cleaned, and we'll respond within 24 hours
                with a personalized, no-obligation quote and answers to any
                questions you may have.
              </p>
            </div>

            <div className="contactpage-form-group">
              <input type="text" id="name" name="name" placeholder=" " required />
              <label htmlFor="name">Your Name</label>
              <div className="contactpage-input-highlight" />
            </div>

            <div className="contactpage-form-group">
              <input
                type="email"
                id="email"
                name="email"
                placeholder=" "
                required
              />
              <label htmlFor="email">Email Address</label>
              <div className="contactpage-input-highlight" />
            </div>

            <div className="contactpage-form-group">
              <input type="tel" id="phone" name="phone" placeholder=" " />
              <label htmlFor="phone">Phone Number</label>
              <div className="contactpage-input-highlight" />
            </div>

            <div className="contactpage-form-group">
              <div className="contactpage-input-with-icon">
                <FaHome className="contactpage-input-icon" />
                <input
                  type="text"
                  id="address"
                  name="address"
                  placeholder=" "
                  required
                />
                <label htmlFor="address">Your Address</label>
                <div className="contactpage-input-highlight" />
              </div>
            </div>

            <div className="contactpage-form-group">
              <textarea
                id="message"
                name="message"
                rows={4}
                placeholder=" "
                required
              ></textarea>
              <label htmlFor="message">Your Message</label>
              <div className="contactpage-input-highlight" />
            </div>

            <div className="contactpage-consent-group">
              <label className="contactpage-consent-label">
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={handleConsentChange}
                  className="contactpage-consent-checkbox"
                />
                <span className="contactpage-consent-text">
                  I consent to being contacted via SMS or Email by Greenworks
                  with information related to my cleaning request, scheduling,
                  and service reminders.
                </span>
              </label>
            </div>

            <div className="contactpage-form-group recaptcha-container">
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={handleCaptchaChange}
                theme="light"
              />
            </div>

            <motion.button
              type="submit"
              className="contactpage-submit-btn"
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.98 }}
              disabled={isLoading || !consentChecked}
            >
              {isLoading ? (
                <span>Sending...</span>
              ) : (
                <>
                  <FaPaperPlane className="contactpage-btn-icon" />
                  <span>Send Message</span>
                  <div className="contactpage-btn-hover-effect" />
                </>
              )}
            </motion.button>

            {error && (
              <div className="contactpage-form-error">{error}</div>
            )}
            {isSubmitted && (
              <motion.div
                className="contactpage-form-success-message"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
              >
                Message sent successfully! Redirecting to thank you page...
              </motion.div>
            )}
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;